import { Box, Card, CardContent, CardHeader, Divider, Typography } from "@mui/material"
import { alpha } from "@mui/material/styles"
import type { Theme } from "@mui/material/styles"
import type { QuoteStatusBreakdown as QuoteStatusBreakdownType } from "@crm-src/types/financial-metrics"
import { formatCurrency } from "@crm-src/lib/utils"
import { FileText, CheckCircle, XCircle, Clock } from "lucide-react"

interface QuoteStatusBreakdownProps {
  data: QuoteStatusBreakdownType
}

// Each status maps to a semantic MUI palette role (inlined during the MUI 5
// migration from the former Tailwind status-class badges). The accent drives
// the icon/label color and a soft alpha tint for the box background.
type PaletteRole = "warning" | "success" | "error"

export function QuoteStatusBreakdown({ data }: QuoteStatusBreakdownProps) {
  const statuses: {
    label: string
    data: QuoteStatusBreakdownType["pending"]
    icon: React.ReactNode
    role: PaletteRole
  }[] = [
    {
      label: 'Pending',
      data: data.pending,
      icon: <Clock size={20} />,
      role: 'warning',
    },
    {
      label: 'Converted',
      data: data.converted,
      icon: <CheckCircle size={20} />,
      role: 'success',
    },
    {
      label: 'Expired',
      data: data.expired,
      icon: <Clock size={20} />,
      role: 'warning',
    },
    {
      label: 'Rejected',
      data: data.rejected,
      icon: <XCircle size={20} />,
      role: 'error',
    }
  ]

  return (
    <Card>
      <CardHeader
        title={
          <Box sx={{ display: "flex", alignItems: "center", fontSize: "1.125rem" }}>
            <Box component="span" sx={{ mr: 1, display: "flex", color: "text.secondary" }}><FileText size={20} /></Box>
            Quote Status Breakdown
          </Box>
        }
      />
      <CardContent sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 2 }}>
          {statuses.map((status) => (
            <Box
              key={status.label}
              sx={{
                p: 2,
                borderRadius: 1,
                border: 1,
                borderColor: (t: Theme) => alpha(t.palette[status.role].main, 0.3),
                bgcolor: (t: Theme) => alpha(t.palette[status.role].main, 0.08),
              }}
            >
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 1 }}>
                <Typography component="span" sx={{ fontSize: "0.875rem", fontWeight: 500, color: `${status.role}.main` }}>
                  {status.label}
                </Typography>
                <Box sx={{ display: "flex", color: `${status.role}.main` }}>{status.icon}</Box>
              </Box>
              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "text.primary" }}>
                  {status.data.count}
                </Typography>
                <Typography sx={{ fontSize: "0.875rem", color: "text.secondary" }}>
                  {formatCurrency(status.data.total_amount, { decimals: 0 })}
                </Typography>
              </Box>
            </Box>
          ))}
        </Box>

        {/* Total Summary */}
        <Box sx={{ pt: 2 }}>
          <Divider sx={{ mb: 2 }} />
          <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <Typography component="span" sx={{ fontSize: "0.875rem", fontWeight: 500, color: "text.secondary" }}>Total Quotes</Typography>
            <Box sx={{ textAlign: "right" }}>
              <Typography sx={{ fontSize: "1.125rem", fontWeight: 700, color: "text.primary" }}>{data.total.count}</Typography>
              <Typography sx={{ fontSize: "0.875rem", color: "text.secondary" }}>{formatCurrency(data.total.total_amount, { decimals: 0 })}</Typography>
            </Box>
          </Box>
        </Box>
      </CardContent>
    </Card>
  )
}
